import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  rosterImport as importApi,
  type ConflictDecision,
  type ConflictField,
  type ImportCommitResult,
  type ImportPreview,
  type ImportRow,
  type ImportRowStatus,
} from '../api';
import { useUnits } from '../units';

const STATUS_LABEL: Record<ImportRowStatus, string> = {
  new: 'New Scout',
  update: 'Update',
  conflict: 'Conflict',
  unchanged: 'No change',
  error: 'Error',
};

export function RosterImport() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole } = useUnits();
  const mayImport = hasRole('ADMIN') || hasRole('DEN_LEADER');

  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ConflictDecision>>({});
  const [result, setResult] = useState<ImportCommitResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const importableCount = useMemo(() => {
    if (!preview) return 0;
    let n = preview.summary.new + preview.summary.update + preview.summary.unchanged;
    for (const row of preview.rows) {
      if (row.status !== 'conflict') continue;
      const d = decisions[String(row.rowNumber)];
      if (d && d !== 'skip' && Object.values(d).some((v) => v === 'csv')) n++;
    }
    return n;
  }, [preview, decisions]);

  if (!mayImport) return <Navigate to="/" replace />;

  async function downloadTemplate() {
    setError(null);
    try {
      const text = await importApi.template(unitId);
      const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scout-roster-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not download the template');
    }
  }

  async function onFile(file: File) {
    setError(null);
    setResult(null);
    setPreview(null);
    setDecisions({});
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
    setBusy(true);
    try {
      setPreview(await importApi.preview(unitId, text));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read that file');
    } finally {
      setBusy(false);
    }
  }

  function setFieldChoice(rowNumber: number, field: ConflictField, value: 'csv' | 'existing' | '') {
    setDecisions((prev) => {
      const key = String(rowNumber);
      const cur = prev[key];
      const obj = cur && cur !== 'skip' ? { ...cur } : {};
      if (value === '') delete obj[field];
      else obj[field] = value;
      return { ...prev, [key]: obj };
    });
  }

  async function commit() {
    if (!csv) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await importApi.commit(unitId, csv, decisions));
      setPreview(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <p className="muted small">
        <Link to={`/units/${unitId}/roster`}>← Roster</Link> · {currentUnit?.name}
      </p>
      <h1>Import roster from CSV</h1>
      <p className="muted">
        Download the template, fill in one row per Scout, then upload it to preview before anything
        is saved. Columns: first_name, last_name, grade, dob, den, guardian_emails, guardian_names.
        Separate multiple guardian emails/names with <code>;</code>. Rows that match an existing
        Scout update that record; guardian emails without an account are invited.
      </p>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={downloadTemplate}>Download template</button>
        <label
          className="primary"
          style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}
        >
          {fileName ? `Choose another file (${fileName})` : 'Choose CSV file'}
          <input
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = '';
            }}
          />
        </label>
        {busy && <span className="muted small">Working…</span>}
      </div>

      {error && <p className="error">{error}</p>}

      {result && (
        <div className="callout ok" style={{ marginTop: 16 }}>
          <strong>Import complete.</strong> {result.created} created, {result.updated} updated,{' '}
          {result.skipped} skipped · {result.guardiansLinked} guardian
          {result.guardiansLinked === 1 ? '' : 's'} linked, {result.guardiansInvited} invited
          {result.guardiansSkipped > 0 && `, ${result.guardiansSkipped} failed`}.{' '}
          <Link to={`/units/${unitId}/roster`}>View roster →</Link>
          {result.rows.some((r) => /skipped|invited|could not/.test(r.outcome)) && (
            <ul className="reqs" style={{ marginTop: 8 }}>
              {result.rows
                .filter((r) => /skipped|invited|could not/.test(r.outcome))
                .map((r) => (
                  <li key={`${r.rowNumber}-${r.outcome}`}>
                    Row {r.rowNumber}: {r.outcome}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {preview && (
        <>
          <div className="row" style={{ gap: 12, alignItems: 'center', margin: '16px 0' }}>
            <SummaryPill label="New" n={preview.summary.new} />
            <SummaryPill label="Update" n={preview.summary.update} />
            <SummaryPill label="Conflict" n={preview.summary.conflict} />
            <SummaryPill label="No change" n={preview.summary.unchanged} />
            <SummaryPill label="Error" n={preview.summary.error} />
          </div>
          {preview.summary.conflict > 0 && (
            <p className="muted small">
              For each conflict, choose the CSV value or keep what’s on file — per field. Untouched
              conflicts are skipped.
            </p>
          )}

          <table className="grid">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Grade</th>
                <th>DOB</th>
                <th>Den</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <PreviewRow
                  key={r.rowNumber}
                  row={r}
                  decision={decisions[String(r.rowNumber)]}
                  onFieldChoice={setFieldChoice}
                />
              ))}
            </tbody>
          </table>

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="primary" disabled={busy || importableCount === 0} onClick={commit}>
              Import {importableCount} row{importableCount === 1 ? '' : 's'}
            </button>
            <button
              onClick={() => {
                setPreview(null);
                setCsv(null);
                setFileName(null);
                setDecisions({});
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PreviewRow({
  row: r,
  decision,
  onFieldChoice,
}: {
  row: ImportRow;
  decision: ConflictDecision | undefined;
  onFieldChoice: (rowNumber: number, field: ConflictField, value: 'csv' | 'existing' | '') => void;
}) {
  const perField = decision && decision !== 'skip' ? decision : {};
  return (
    <tr>
      <td className="muted small">{r.rowNumber}</td>
      <td>
        {r.firstName} {r.lastName}
      </td>
      <td>{r.grade ?? '—'}</td>
      <td>{r.dob ?? '—'}</td>
      <td>{r.denName ?? '—'}</td>
      <td>
        <span className="badge">{STATUS_LABEL[r.status]}</span>
      </td>
      <td className="small">
        {r.errors.map((e) => (
          <div key={e} className="error">
            {e}
          </div>
        ))}
        {r.changes.map((c) =>
          c.conflict ? (
            <div key={c.field} style={{ margin: '2px 0' }}>
              <span className="error">{c.field}</span>: keep <em>{c.from ?? '∅'}</em> or use{' '}
              <em>{c.to ?? '∅'}</em>?{' '}
              <select
                value={perField[c.field] ?? ''}
                onChange={(e) =>
                  onFieldChoice(r.rowNumber, c.field, e.target.value as 'csv' | 'existing' | '')
                }
              >
                <option value="">— choose —</option>
                <option value="existing">Keep “{c.from ?? '∅'}”</option>
                <option value="csv">Use “{c.to ?? '∅'}”</option>
              </select>
            </div>
          ) : (
            <div key={c.field} className="muted">
              {c.field}: {c.from ?? '∅'} → {c.to ?? '∅'}
            </div>
          ),
        )}
        {r.guardians.map((g) => (
          <div key={g.email} className="muted">
            guardian {g.email}
            {g.alreadyLinked
              ? ' (already linked)'
              : g.accountExists
                ? ' (will link)'
                : ' (will be invited)'}
          </div>
        ))}
      </td>
    </tr>
  );
}

function SummaryPill({ label, n }: { label: string; n: number }) {
  return (
    <span className="muted small">
      <strong style={{ color: n > 0 ? 'var(--fg)' : undefined }}>{n}</strong> {label}
    </span>
  );
}
